<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Billing History</h5>
          <p class="page-sub">Manage and track all billing records</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search billings..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="JSON.parse(status.pending)">Created</option>
            <option :value="JSON.parse(status.active)">Active</option>
            <option :value="JSON.parse(status.completed)">Completed</option>
            <option :value="JSON.parse(status.all)">All Statuses</option>
          </select>
  
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'l', 'billings', 'Billings')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('billings.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle"
              :class="selectedModel.length === 0 || permission.delete_billing != 1 ? 'btn-outline--disabled' : ''"
              data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="tasks" /> Bulk Action
              <span v-if="selectedModel.length > 0" class="bulk-count">{{ selectedModel.length }}</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <button class="dropdown-item dropdown-item--danger"
                  :disabled="permission.delete_billing != 1 || selectedModel.length === 0"
                  data-bs-toggle="modal" data-bs-target="#bulkModal">
                  <font-awesome-icon icon="trash" class="me-2" /> Delete Selected
                </button>
              </li>
            </ul>
          </div>
  
          <router-link :to="$url('/create-billing')" class="btn-primary-action"
            :class="permission.create_billing != 1 ? 'disabled' : ''">
            <font-awesome-icon icon="plus" /> Create Billing
          </router-link>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading billings...</p>
        </div>
  
        <div v-else-if="billings.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="file-invoice-dollar" /></div>
          <p class="state-title">No billings found</p>
          <p class="state-sub">Your billing list is empty. Create your first billing to get started.</p>
          <router-link v-if="permission.create_billing == 1" :to="$url('/create-billing')" class="btn-primary-action mt-3">
            <font-awesome-icon icon="plus" /> Create Billing
          </router-link>
        </div>
  
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
              <span v-if="selectedModel.length > 0" class="selected-hint">· {{ selectedModel.length }} selected</span>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option><option value="10">10</option><option value="20">20</option>
                <option value="30">30</option><option value="50">50</option><option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input type="checkbox" class="row-check" v-model="allSelected" @change="selectAll" />
                  </th>
                  <th>#</th>
                  <th>Reference</th>
                  <th>Billing Group</th>
                  <th class="text-right">Invoice Amt</th>
                  <th class="text-right">Total</th>
                  <th class="text-right">Paid</th>
                  <th class="text-right">Balance</th>
                  <th>Status</th>
                  <th>Billing Date</th>
                  <th>Created By</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in billings" :key="index" :class="selectedModel.includes(row.id) ? 'row--selected' : ''">
  
                  <td class="col-check">
                    <input type="checkbox" class="row-check" v-model="selectedModel" :value="row.id" />
                  </td>
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <td><span class="batch-code">{{ row.reference }}</span></td>
  
                  <td>
                    <span class="group-pill" v-if="row.group?.name">{{ row.group.name }}</span>
                    <span class="text-muted-cell" v-else>—</span>
                  </td>
  
                  <td class="text-right text-muted-cell">{{ row?.currency?.symbol }}{{ formatPrice(row.invoice_amount) }}</td>
                  <td class="text-right fw-bold">{{ row?.currency?.symbol }}{{ formatPrice(row.total_amount) }}</td>
                  <td class="text-right earning-value fw-bold">{{ row?.currency?.symbol }}{{ formatPrice(row.paid_amount) }}</td>
                  <td class="text-right" :class="row.balance_amount > 0 ? 'deduction-value fw-bold' : 'text-muted-cell'">
                    {{ row?.currency?.symbol }}{{ formatPrice(row.balance_amount) }}
                  </td>
  
                  <td>
                    <span class="status-badge" :class="statusClass(row.status)">{{ getStatus(row) }}</span>
                  </td>
  
                  <td class="text-muted-cell" style="white-space:nowrap">{{ formatDate(row.billing_date) }}</td>
  
                  <td>
                    <div class="creator-cell" v-if="row.user">
                      <div class="creator-avatar">{{ creatorInitials(row.user) }}</div>
                      <span class="creator-name">{{ row.user.first_name }} {{ row.user.last_name }}</span>
                    </div>
                    <span v-else class="text-muted-cell">—</span>
                  </td>
  
                  <td class="col-actions">
                    <div class="dropdown">
                      <button class="action-dropdown-btn" type="button"
                        data-bs-toggle="dropdown" data-bs-strategy="fixed" aria-expanded="false">
                        <font-awesome-icon icon="ellipsis-v" />
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end action-menu">
                        <li>
                          <router-link class="action-menu-item" :to="$url('/billing/detail/' + row.id)"
                            :class="permission.view_billing != 1 ? 'action-menu-item--disabled' : ''">
                            <font-awesome-icon icon="eye" class="action-menu-icon action-menu-icon--view" />
                            View Invoices
                          </router-link>
                        </li>
                        <li v-if="row.paid_amount <= 0 && permission.delete_billing == 1">
                          <hr class="dropdown-divider" />
                          <button class="action-menu-item action-menu-item--danger"
                            @click="getOneModal(row)"
                            data-bs-toggle="modal" data-bs-target="#singleModal">
                            <font-awesome-icon icon="trash" class="action-menu-icon" />
                            Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
  
        </template>
      </div>
  
  
      <SingleDeleteModal :modalId="'singleModal'" @deleteSingle="deleteBilling"
        :modalText="'Are you sure you want to delete this billing?'" :action="'deleteSingle'" />
  
      <BulkDeleteModal :modalId="'bulkModal'" @deleteBulk="bulkBillingDelete"
        :modalText="'Are you sure you want to delete the selected billings?'" />
  
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import { useStore } from 'vuex';
  import moment from 'moment';
  import Header from './component/header.vue';
  import BulkDeleteModal  from '../../../components/bulkDeleteModal.vue';
  import SingleDeleteModal from '../../../components/ActionModal.vue';
  
  export default {
    components: { Header, BulkDeleteModal, SingleDeleteModal },
    mixins: [reusableMixin],
  
    data() {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  
      return {
        print_content: [],
        headerArrayColumn: ['S/N','Reference','Billing Group','Currency','Total Amount','Total Paid','Total Balance','Created Date','Due Date','Status'],
        billings:      [],
        billing_id:    null,
        loading:       false,
        currentData:   {},
        allSelected:   false,
        selectedModel: [],
  
        status: {
          all:       "['0','1','2']".replace(/'/g, '"'),
          pending:   "['0']".replace(/'/g, '"'),
          active:    "['1']".replace(/'/g, '"'),
          completed: "['2']".replace(/'/g, '"'),
        },
  
        tableData: { page: 1,
          start_date: sixMonthsAgo, end_date: new Date(),
          draw: 0, length: 10, search: '', column: 0, dir: 'desc', status: [0,1,2],
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast:  useToast(),
        store:  useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    created() { this.fetchData(); },
  
    methods: {
      getStatus(data) {
        return ['Created','Active','Completed'][data.status] ?? '—';
      },
  
      statusClass(status) {
        return ['status--created','status--active','status--completed'][status] ?? '';
      },
  
      creatorInitials(user) {
        return ((user.first_name?.[0] ?? '') + (user.last_name?.[0] ?? '')).toUpperCase();
      },
  
      dataToPrint(details) {
        this.print_content = details.map((b, idx) => ({
          sn:             idx + 1,
          reference:      b.reference,
          group:          b?.group?.name,
          currency:       `${b?.currency?.name} (${b?.currency?.code})`,
          total_amount:   this.formatPrice(b.total_amount),
          paid_amount:    this.formatPrice(b.paid_amount),
          balance_amount: this.formatPrice(b.balance_amount),
          created_date:   this.formatDate(b.created_at),
          due_date:       this.formatDate(b.due_date),
          status:         this.getStatus(b),
        }));
      },
  
      async fetchData(url = '/billing/all') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.billings = response.data.data.data;
            this.dataToPrint(this.billings);
            this.configPagination(response.data.data);
            this.loading = false;
          })
          .catch(() => { this.loading = false; });
      },
  
      getOneModal(data)    { this.billing_id = data.id; },
  
      selectAll() {
        this.selectedModel = this.allSelected ? this.billings.map(b => b.id) : [];
      },
  
      async deleteBilling() {
        axios.delete(`/billing/delete/${this.billing_id}`)
          .then(response => {
            if (response.data.status == 200) { this.fetchData(); this.toast.success(response.data.message); }
            else { this.toast.error(response.data.message); }
          })
          .catch(() => {});
      },
  
      bulkBillingDelete() {
        if (!this.selectedModel.length) { this.toast.error('Nothing was selected!'); return; }
        axios.post('/billing/delete-bulk', { billings: this.selectedModel })
          .then(response => {
            this.fetchData(); this.toast.success(response.data.message); this.selectedModel = [];
          })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Bulk delete failed'); });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
  
      formatDate(date) { return moment(date).format('MMM DD, YYYY'); },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 });
      },
    },
  };
  </script>
  
  <style scoped>
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
  .btn-primary-action.disabled { opacity:.5; pointer-events:none; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; position:relative; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline--disabled { opacity:.5; pointer-events:none; }
  .btn-outline::after { display:none !important; }
  .me-2 { margin-right:8px; } .mt-3 { margin-top:12px; }
  
  /* Bulk count badge */
  .bulk-count { display:inline-flex; align-items:center; justify-content:center; background:#004AAD; color:#fff; border-radius:10px; font-size:11px; font-weight:700; padding:1px 6px; margin-left:4px; }
  
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell { color:#4a5e78; }
  
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .selected-hint { color:#004AAD; font-weight:600; }
  .toolbar-right { display:flex; align-items:center; gap:8px; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody tr.row--selected { background:#edf4ff; }
  .data-table tbody td { padding:12px 14px; font-size:13px; vertical-align:middle; }
  .col-check  { width:40px; padding-left:16px !important; }
  .col-actions { width:48px; text-align:center; }
  .text-right { text-align:right; }
  .fw-bold    { font-weight:700; }
  .earning-value   { color:#16a34a; }
  .deduction-value { color:#e03535; }
  
  /* ── Checkbox ── */
  .row-check { width:15px; height:15px; cursor:pointer; accent-color:#004AAD; }
  
  /* ── Batch Code ── */
  .batch-code { font-family:monospace; font-size:12px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  
  /* ── Group Pill ── */
  .group-pill { display:inline-block; background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; border-radius:6px; padding:2px 10px; font-size:12px; font-weight:600; }
  
  /* ── Status Badges ── */
  .status-badge       { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; white-space:nowrap; }
  .status--created    { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .status--active     { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .status--completed  { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  
  /* ── Creator Cell ── */
  .creator-cell   { display:flex; align-items:center; gap:8px; }
  .creator-avatar { width:28px; height:28px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; flex-shrink:0; }
  .creator-name   { font-size:13px; color:#1a2b45; }
  
  /* ── Action Dropdown ── */
  .action-dropdown-btn { width:30px; height:30px; border-radius:8px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:13px; color:#7a90b0; cursor:pointer; transition:background .15s; margin:auto; }
  .action-dropdown-btn:hover { background:#f4f7fb; color:#1a2b45; }
  .action-menu { min-width:175px; padding:6px; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); }
  .action-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:8px 12px; font-size:13px; color:#1a2b45; background:transparent; border:none; border-radius:7px; cursor:pointer; text-decoration:none; transition:background .12s; }
  .action-menu-item:hover:not(:disabled):not(.action-menu-item--disabled) { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
  .action-menu-item--disabled { opacity:.4; cursor:not-allowed; pointer-events:none; }
  .action-menu-item--danger   { color:#e03535; } .action-menu-item--danger:hover { background:#fff0f0 !important; }
  .action-menu-icon       { font-size:12px; width:16px; flex-shrink:0; }
  .action-menu-icon--view { color:#0d9488; }
  
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; background:transparent; border:none; width:100%; text-align:left; cursor:pointer; }
  .dropdown-item:hover:not(:disabled) { background:#f4f7fb; }
  .dropdown-item--danger { color:#e03535; } .dropdown-item--danger:hover { background:#fff0f0 !important; }
  .dropdown-item:disabled { opacity:.4; cursor:not-allowed; }
  .dropdown-divider { border-color:#f0f4f8; margin:4px 0; }
  
  @media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .search-input { width:100%; } }
  </style>